import React, { useEffect, useState } from "react";
import { api } from "@/planner/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Minus, Plus, Trash2 } from "lucide-react";

// Change one machine's rows (PUT /machines/{id}/layout): slots per row, depth and name. The machine's
// template stays as it is. Each row remembers which row it was (from_row) so its products stay put.
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const rowsOf = (machine) => {
  const counts = {};
  (machine.slots || []).forEach((s) => { const l = (String(s.slot_id).match(/^[A-Z]+/) || [""])[0]; counts[l] = (counts[l] || 0) + 1; });
  const layout = machine.layout || [];
  const letters = Object.keys(counts).sort();
  return letters.map((l) => {
    const r = layout[LETTERS.indexOf(l)] || {};
    return { from_row: l, slots: counts[l], depth: r.depth ?? 10, label: r.label || "", color: r.color || "" };
  });
};

export default function MachineRowsDialog({ machine, open, onClose, onSaved, beforeSave }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setRows(rowsOf(machine)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  // Slots that lose their product (or count) when a row gets shorter or goes away.
  const lost = (machine.slots || []).filter((s) => {
    if (!s.product_id && !s.current) return false;
    const [, l, n] = String(s.slot_id).match(/^([A-Z]+)(\d+)$/) || [];
    const row = rows.find((r) => r.from_row === l);
    return !row || Number(n) > (Number(row.slots) || 0);
  });

  const save = async () => {
    if (lost.length && !window.confirm(`${lost.map((s) => s.slot_id).join(", ")} ${lost.length === 1 ? "has" : "have"} a product and will be removed. Continue?`)) return;
    setSaving(true);
    try {
      await beforeSave?.();
      const { data } = await api.put(`/machines/${machine.id}/layout`, { layout: rows.map((r) => ({
        slots: Math.max(0, Math.min(26, Math.round(Number(r.slots) || 0))), depth: Math.max(0, Math.round(Number(r.depth) || 0)),
        label: r.label, color: r.color, from_row: r.from_row || null })) });
      toast.success("Rows updated");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change the rows."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="machine-rows-dialog">
        <DialogHeader>
          <DialogTitle>Rows in {machine.name}</DialogTitle>
          <DialogDescription>How many items fit across each row, and how deep. Only this machine changes; its template stays the same.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-[2rem_1fr_7.5rem_4.5rem_2rem] items-center gap-2 text-[11px] font-semibold uppercase text-muted-foreground">
            <span>Row</span><span>Name</span><span className="text-center">Items across</span><span className="text-center">Deep</span><span />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[2rem_1fr_7.5rem_4.5rem_2rem] items-center gap-2" data-testid={`machine-row-${i}`}>
              <span className="font-mono font-semibold">{LETTERS[i]}</span>
              <Input className="h-9" value={r.label} placeholder="e.g. Chips" onChange={(e) => set(i, { label: e.target.value })} />
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" aria-label="One less" onClick={() => set(i, { slots: Math.max(1, (Number(r.slots) || 1) - 1) })}><Minus className="h-4 w-4" /></Button>
                <Input className="h-9 px-1 text-center" type="number" min="1" max="26" inputMode="numeric" value={r.slots} onChange={(e) => set(i, { slots: e.target.value })} data-testid={`machine-row-slots-${i}`} />
                <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" aria-label="One more" onClick={() => set(i, { slots: Math.min(26, (Number(r.slots) || 0) + 1) })} data-testid={`machine-row-more-${i}`}><Plus className="h-4 w-4" /></Button>
              </div>
              <Input className="h-9 text-center" type="number" min="0" inputMode="numeric" value={r.depth} onChange={(e) => set(i, { depth: e.target.value })} title="Default par for new slots in this row" />
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={`Remove row ${LETTERS[i]}`} disabled={rows.length <= 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="w-full" disabled={rows.length >= 26} onClick={() => setRows([...rows, { from_row: null, slots: 6, depth: 10, label: "", color: "" }])} data-testid="machine-row-add"><Plus className="mr-1 h-4 w-4" />Add row</Button>
          {lost.length > 0 && <p className="text-xs text-amber-600" data-testid="machine-rows-lost">{lost.map((s) => s.slot_id).join(", ")} will be removed with {lost.length === 1 ? "its product" : "their products"}.</p>}
          <p className="text-xs text-muted-foreground">Fewer items across drops slots from the right end of the row. New slots start empty with the row's depth as their par.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="machine-rows-save">{saving ? "Saving…" : "Save rows"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
